import React from 'react';
import { Space, Tooltip } from 'antd';
import { RiskSymbolCell } from '@client/components/ui/table/TableCells';
import RiskIndexDisplay from '@client/components/ui/RiskIndexDisplay';
import deleteIcon from '../../../../img/client/icons/delete.svg';
import duplicateIcon from '../../../../img/client/icons/duplicate.svg';
import editIcon from '../../../../img/client/icons/edit.svg';
import documentIcon from '../../../../img/client/icons/document.svg';
import { buildLabelUrl } from '../../../../client/utils/urlUtils';

/**
 * Action button component with circular icon style
 * Matches the design from Figma
 */
const ActionButton = ({ icon, alt, tooltip, onClick }) => (
  <Tooltip title={tooltip}>
    <button
      onClick={onClick}
      className="flex w-8 h-8 items-center justify-center rounded-full border cursor-pointer bg-white hover:bg-gray-50"
      style={{ border: '1px solid #7CB305' }}
    >
      <img
        src={icon}
        alt={alt}
        width="16"
        height="16"
        className="pointer-events-none"
      />
    </button>
  </Tooltip>
);

/**
 * Product cell component - displays product name, registration number, and active substances
 * Matches Figma design tokens exactly:
 * - Product name: color #105235, font-weight 700, font-size 12px
 * - Registration number: color #000, font-weight 400, font-size 12px
 * - Active substances: color #105235, font-weight 400, font-size 12px
 */
const ProductCell = ({ products }) => {
  if (!products || products.length === 0) return <span>-</span>;

  return (
    <div>
      {products.map((product, index) => {
        const productTypeNames = product.productTypes
          ?.map((pt) => pt?.name)
          .filter(Boolean)
          .join(', ');

        return (
          <React.Fragment key={product.id || index}>
            {/* Plus sign separator for multiple products */}
            {index > 0 && (
              <div className="flex justify-center py-1">
                <div className="w-4 h-4 flex justify-center items-center rounded-full bg-[#E9F1D9] text-black text-xs font-medium">
                  +
                </div>
              </div>
            )}

            {/* Product name and registration number */}
            <div className="text-xs leading-[125%] font-roboto">
              <span className="text-[#105235] font-bold">{product.name}</span>
              {product.approvalNumber && (
                <span className="text-black font-normal">
                  {' '}
                  ({product.approvalNumber})
                </span>
              )}
            </div>

            {/* Product types */}
            {productTypeNames && (
              <div className="text-black font-normal text-xs leading-[125%] font-roboto">
                ({productTypeNames})
              </div>
            )}

            {/* Active substances */}
            {product.activeSubstances &&
              product.activeSubstances.length > 0 && (
                <div className="text-[#105235] font-normal text-xs leading-[125%]">
                  {product.activeSubstances.join(' / ')}
                </div>
              )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/**
 * Label/Étiquette cell component - document icon that links to product label
 * Aligns with ProductCell - uses same spacing pattern as the "+" separator
 */
const LabelCell = ({ products, tooltip }) => {
  if (!products || products.length === 0) return <span>-</span>;

  const handleViewLabel = (approvalNumber) => {
    if (approvalNumber) {
      window.open(
        buildLabelUrl(approvalNumber),
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  return (
    <div>
      {products.map((product, index) => (
        <React.Fragment key={product.id || index}>
          {/* Separator space aligned with "+" in ProductCell */}
          {index > 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: '4px 0',
                height: '20px',
              }}
            >
              {/* Empty space to align with "+" separator */}
            </div>
          )}

          {/* Document icon - clickable to open product label */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '32px',
            }}
          >
            {product.approvalNumber ? (
              <Tooltip title={tooltip}>
                <button
                  onClick={() => handleViewLabel(product.approvalNumber)}
                  className="flex items-center justify-center rounded-lg border border-[#D9D9D9] bg-white cursor-pointer transition-all hover:border-[#7CB305] hover:bg-[#F6FFED]"
                  style={{
                    width: '34px',
                    height: '34px',
                    borderRadius: '8px',
                    border: '1px solid #D9D9D9',
                    background: '#fff',
                  }}
                >
                  <img
                    src={documentIcon}
                    alt="Étiquette"
                    width="20"
                    height="20"
                    className="pointer-events-none"
                  />
                </button>
              </Tooltip>
            ) : (
              <span style={{ color: '#8C8C8C' }}>-</span>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Dosages cell component - displays listed dosages for each product
 * Aligns with ProductCell - uses same spacing pattern as the "+" separator
 */
const DosagesCell = ({ products, noDosageText }) => {
  if (!products || products.length === 0) return <span>-</span>;
  return (
    <div className="registered-dosage">
      {products.map((product, index) => (
        <React.Fragment key={product.id || index}>
          {/* Line separator between products */}
          {index > 0 && <div className="h-[1px] bg-[#BFBFBF] my-2" />}

          {/* Dosage content */}
          <div className="min-h-[32px] flex items-center text-xs text-[#595959]">
            {product.listedDosages || noDosageText}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Applied dose cell component - displays dose and unit for each product
 * Aligns with ProductCell - uses same spacing pattern as the "+" separator
 */
const AppliedDoseCell = ({ products }) => {
  if (!products || products.length === 0) return <span>-</span>;

  return (
    <div>
      {products.map((product, index) => (
        <React.Fragment key={product.id || index}>
          {/* Line separator between products */}
          {index > 0 && <div className="h-[1px] bg-[#BFBFBF] my-2" />}

          {/* Dose and unit content */}
          <div className="min-h-[32px] flex items-center text-xs">
            {product.dose} {product.unit?.name || ''}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Risk index cell component (IRS/IRE) - styled box with value
 * Figma design: 32x32px box, rounded corners, neutral background
 */
const RiskIndexCell = ({ value }) => {
  if (value === null || value === undefined) return <span>-</span>;

  return (
    <div className="px-2 py-1 flex justify-center items-center gap-2 rounded-[8px] bg-[#F5F5F5] text-black font-roboto text-xs font-medium leading-[125%]">
      {value}
    </div>
  );
};

/**
 * Factory function for creating table columns for risk calculator results
 * Matches Figma design pixel-perfect
 *
 * @param {Function} t - Translation function
 * @param {Object} handlers - Action handlers (onEdit, onDuplicate, onDelete)
 * @param {boolean} readOnly - Whether to hide the actions column
 */
export const createTableColumns = (t, handlers = {}, readOnly = false) => {
  const columns = [];

  // Only add actions column if not in read-only mode
  if (!readOnly) {
    columns.push({
      title: <div className="vertical-header-text">{t('col_actions')}</div>,
      key: 'actions',
      fixed: 'left',
      width: 60,
      align: 'center',
      className: 'vertical-cell',
      render: (_, record) => (
        <Space
          direction="vertical"
          size={4}
          align="center"
          style={{ width: '100%' }}
        >
          <ActionButton
            icon={duplicateIcon}
            alt={t('action_duplicate')}
            tooltip={t('action_duplicate')}
            onClick={() => handlers.onDuplicate?.(record)}
          />
          <ActionButton
            icon={editIcon}
            alt={t('action_edit')}
            tooltip={t('action_edit')}
            onClick={() => handlers.onEdit?.(record)}
          />
          <ActionButton
            icon={deleteIcon}
            alt={t('action_delete')}
            tooltip={t('action_delete')}
            onClick={() => handlers.onDelete?.(record)}
          />
        </Space>
      ),
    });
  }

  // Add remaining columns
  columns.push(
    {
      title: <span className="text-[10px]">{t('col_culture')}</span>,
      dataIndex: ['culture', 'name'],
      className: 'text-center',
      key: 'culture',
      align: 'center',
      width: 160,
    },
    {
      title: <span className="text-[10px]">{t('col_treatment_type')}</span>,
      dataIndex: ['treatmentType', 'name'],
      key: 'treatmentType',
      align: 'center',
      width: 140,
    },
    {
      title: (
        <span className="text-[10px]">{t('col_application_technique')}</span>
      ),
      dataIndex: ['applicationTechnique', 'name'],
      key: 'applicationTechnique',
      align: 'center',
      width: 140,
    },
    {
      title: (
        <div>
          <div className="text-[10px]">{t('col_commercial_products')}</div>
          <div className="text-[10px] font-normal">
            {t('col_active_substances')}
          </div>
        </div>
      ),
      key: 'products_and_substances',
      align: 'center',
      width: 250,
      render: (_, record) => <ProductCell products={record.products} />,
    },
    {
      title: <div className="vertical-header-text">{t('col_labels')}</div>,
      key: 'labels',
      width: 60,
      align: 'center',
      className: 'vertical-cell',
      render: (_, record) => (
        <LabelCell
          products={record.products}
          tooltip={t('products_label_tooltip')}
        />
      ),
    },
    {
      title: <span className="text-[10px]">{t('col_dosages_listed')}</span>,
      key: 'dosages_listed',
      align: 'center',
      width: 160,
      render: (_, record) => (
        <DosagesCell
          products={record.products}
          noDosageText={t('products_no_dosage')}
        />
      ),
    },
    {
      title: (
        <div>
          <div className="text-[10px]">{t('col_applied_dose')}</div>
          <div className="text-[10px] font-normal">{t('col_unit')}</div>
        </div>
      ),
      key: 'applied_dose',
      align: 'center',
      width: 120,
      render: (_, record) => <AppliedDoseCell products={record.products} />,
    },
    {
      title: <div className="py-[10px]">{t('col_risks_health')}</div>,
      children: [
        {
          title: <div className="vertical-header-text">{t('col_irs')}</div>,
          key: 'health_risk_index',
          className: 'vertical-cell risk-irs-ire',
          width: 50,
          align: 'center',
          render: (_, record) => (
            <RiskIndexDisplay
              value={record.results?.health_risk_index}
              isExpert={record.results?.health_risk_index_is_expert}
              variant="badge"
            />
          ),
        },
        {
          title: (
            <div className="vertical-header-text">
              {t('col_acute_toxicity')}
            </div>
          ),
          key: 'acute_toxicity_symbol',
          className: 'vertical-cell',
          render: (_, record) => (
            <RiskSymbolCell
              symbolData={record.symbols?.health?.acute_toxicity_symbol}
              alt={t('col_acute_toxicity')}
            />
          ),
          width: 50,
          align: 'center',
        },
        {
          title: (
            <div className="vertical-header-text">
              {t('col_long_term_effects')}
            </div>
          ),
          key: 'long_term_effect_symbol',
          className: 'vertical-cell',
          render: (_, record) => (
            <RiskSymbolCell
              symbolData={record.symbols?.health?.long_term_effect_symbol}
              alt={t('col_long_term_effects')}
            />
          ),
          width: 50,
          align: 'center',
        },
      ],
    },
    {
      title: <div className="py-[10px]">{t('col_risks_environment')}</div>,
      children: [
        {
          title: <div className="vertical-header-text">{t('col_ire')}</div>,
          key: 'environment_risk_index',
          className: 'vertical-cell risk-irs-ire',
          width: 50,
          align: 'center',
          render: (_, record) => (
            <RiskIndexDisplay
              value={record.results?.environment_risk_index}
              isExpert={record.results?.environment_risk_index_is_expert}
              variant="badge"
            />
          ),
        },
        {
          title: <div className="vertical-header-text">{t('col_fish')}</div>,
          key: 'fishes_symbol',
          className: 'vertical-cell',
          render: (_, record) => (
            <RiskSymbolCell
              symbolData={record.symbols?.environment?.fishes_symbol}
              alt={t('col_fish')}
            />
          ),
          width: 50,
          align: 'center',
        },
        {
          title: <div className="vertical-header-text">{t('col_birds')}</div>,
          key: 'birds_symbol',
          className: 'vertical-cell',
          render: (_, record) => (
            <RiskSymbolCell
              symbolData={record.symbols?.environment?.birds_symbol}
              alt={t('col_birds')}
            />
          ),
          width: 50,
          align: 'center',
        },
        {
          title: <div className="vertical-header-text">{t('col_bees')}</div>,
          key: 'bees_symbol',
          className: 'vertical-cell',
          render: (_, record) => (
            <RiskSymbolCell
              symbolData={record.symbols?.environment?.bees_symbol}
              alt={t('col_bees')}
            />
          ),
          width: 50,
          align: 'center',
        },
        {
          title: (
            <div className="vertical-header-text">{t('col_persistence')}</div>
          ),
          key: 'persistence_symbol',
          className: 'vertical-cell',
          render: (_, record) => (
            <RiskSymbolCell
              symbolData={record.symbols?.environment?.persistence_symbol}
              alt={t('col_persistence')}
            />
          ),
          width: 50,
          align: 'center',
        },
        {
          title: (
            <div className="vertical-header-text">{t('col_leaching')}</div>
          ),
          key: 'leaching_symbol',
          className: 'vertical-cell',
          render: (_, record) => (
            <RiskSymbolCell
              symbolData={record.symbols?.environment?.leaching_symbol}
              alt={t('col_leaching')}
            />
          ),
          width: 50,
          align: 'center',
        },
      ],
    }
  );

  return columns;
};
